import { faClone, faRightLeft } from "@fortawesome/pro-duotone-svg-icons"
import { faCirclePause, faCirclePlay, faMemoPad } from "@fortawesome/pro-regular-svg-icons"
import { faCircleStop, faTrash } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { CAMPAIGN_STATUSES, ICampaign } from "campaign/model"
import { cancelCampaign } from "campaign/tools/cancel"
import { isCampaignPausable, isCampaignStopable } from "campaign/tools/common"
import { deleteCampaign } from "campaign/tools/delete"
import { toggleDspDealStatus } from "campaign/tools/dsp-deal"
import { openDuplicatePopup } from "campaign/tools/duplicate"
import { setCampaignPause } from "campaign/tools/save"
import { CAMPAIGN_SUMMARY_PATH, DSP_DEAL_SUMMARY_PATH, FORECAST_SUMMARY_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { canSaveCampaigns } from "user/tools/permissions"
import { ListActionButton } from "../../../ui/components/main-layout/ListActionButton"

interface IListActionsProps {
  campaign: ICampaign
  isForecast: boolean
  isDspDeal: boolean
}

export const ListActions = React.memo(({ campaign, isForecast, isDspDeal }: IListActionsProps): JSX.Element => {
  const canSave = canSaveCampaigns()
  const canPause = canSave && isCampaignPausable(campaign.status_id, isForecast, isDspDeal)
  const canStop = canSave && isCampaignStopable(campaign.status_id)
  const isDraft = campaign.status_id === CAMPAIGN_STATUSES.PENDING
  const canDelete = (canSave && isDraft) || isForecast
  const isDspDealValid = !!campaign.dspDeal
  return (
    <FlexBox
      sx={{
        gap: ".5rem",
        fontSize: "1.5rem"
      }}>
      <ListActionButton
        id={`campaign-${campaign.id}-edit-button`}
        icon={faMemoPad}
        disabled={isDspDeal && !isDspDealValid}
        tooltip={translate(
          isForecast
            ? "forecast.summary.tooltip.title"
            : isDspDeal
              ? "dsp-deal.summary.tooltip.title"
              : "campaign.summary.tooltip.title"
        )}
        onClick={() =>
          navigate({
            path: isForecast ? FORECAST_SUMMARY_PATH : isDspDeal ? DSP_DEAL_SUMMARY_PATH : CAMPAIGN_SUMMARY_PATH,
            pathParams: campaign.id
          })
        }
      />
      {!isForecast && !isDspDeal && (
        <>
          <ListActionButton
            id={`campaign-${campaign.id}-pause-button`}
            icon={canPause && campaign.is_paused ? faCirclePlay : faCirclePause}
            tooltip={translate(
              !canPause
                ? "campaign.pause.disabled"
                : campaign.is_paused
                  ? "campaign.unpause.title"
                  : "campaign.pause.title"
            )}
            disabled={!canPause}
            onClick={() => setCampaignPause(campaign.id, campaign.product || "", !Boolean(campaign.is_paused), true)}
          />
          <ListActionButton
            id={`campaign-${campaign.id}-cancel-button`}
            tooltip={translate(canStop ? "campaign.cancel.title" : "campaign.cancel.disabled")}
            icon={faCircleStop}
            disabled={!canStop}
            onClick={() => cancelCampaign(campaign.id, campaign.product || "-", isForecast)}
          />
        </>
      )}
      {!isDspDeal && (
        <ListActionButton
          id={`campaign-${campaign.id}-duplicate-campaign-button`}
          tooltip={translate(isForecast ? "forecast.duplicate.tooltip" : "campaign.duplicate.tooltip")}
          icon={faClone}
          disabled={!canSave}
          onClick={() => openDuplicatePopup(campaign)}
        />
      )}
      {isForecast && (
        <ListActionButton
          id={`campaign-${campaign.id}-duplicate-forecast-button`}
          tooltip={translate("forecast.convert.tooltip")}
          icon={faRightLeft}
          onClick={() => openDuplicatePopup(campaign, true)}
        />
      )}
      {!isDspDeal && (
        <ListActionButton
          id={`campaign-${campaign.id}-delete-button`}
          tooltip={translate(canDelete ? "campaign.delete.title" : "campaign.delete.disabled")}
          icon={faTrash}
          disabled={!canDelete}
          onClick={() => deleteCampaign(campaign.id, campaign.product || "-", isForecast)}
        />
      )}
      {isDspDeal && (
        <ListActionButton
          id={`campaign-${campaign.id}-disable-button`}
          tooltip={translate(
            CAMPAIGN_STATUSES.ACTIVE === campaign.status_id
              ? "dsp-deal.deactivate.title"
              : CAMPAIGN_STATUSES.CANCELLED === campaign.status_id
                ? "dsp-deal.activate.title"
                : "dsp-deal.disabled.title"
          )}
          icon={CAMPAIGN_STATUSES.CANCELLED === campaign.status_id ? faCirclePlay : faCirclePause}
          disabled={
            !isDspDealValid ||
            (CAMPAIGN_STATUSES.ACTIVE !== campaign.status_id && CAMPAIGN_STATUSES.CANCELLED !== campaign.status_id)
          }
          onClick={() => toggleDspDealStatus(campaign, true)}
        />
      )}
    </FlexBox>
  )
})
